import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, TextField, Alert, CircularProgress, Paper } from '@mui/material';
import LoginLayout from '../../components/LoginLayout/LoginLayout';
import { studentRegistrationService, VerifyTicketResult } from '../../services/studentRegistrationService';

// Page publique — pas de garde d'authentification : les étudiant·es qui l'utilisent n'ont pas
// encore de moyen de se connecter normalement (voir chantier "Admin Utilisateurs"). Le token
// obtenu après vérification du ticket CAS reste en état local du composant, jamais dans
// AuthProvider : ce n'est pas une session applicative.
const StudentRegistrationPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verified, setVerified] = useState<VerifyTicketResult | null>(null);
  const [gitlabUsername, setGitlabUsername] = useState('');
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ticket = params.get('ticket');
    if (!ticket) return;

    setLoading(true);
    setError(null);
    studentRegistrationService.verifyTicket(ticket)
      .then((result) => {
        setVerified(result);
        // Le backend tente déjà l'auto-complétion (login CAS = pseudo GitLab confirmé sur ce
        // périmètre, vérifié via l'API GitLab côté serveur) : si gitlab_username revient déjà
        // renseigné, l'inscription est terminée sans aucune saisie. Sinon (cas rare : compte
        // GitLab pas encore créé, pseudo réellement différent), on retombe sur le formulaire,
        // pré-rempli avec le login CAS comme point de départ.
        if (result.gitlab_username) {
          setCompleted(true);
        } else {
          setGitlabUsername(result.login);
        }
      })
      .catch((e: any) => setError(e.message || "Erreur lors de la vérification du ticket CAS"))
      .finally(() => setLoading(false));
  }, []);

  const handleCasLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = await studentRegistrationService.getCasLoginUrl();
      window.location.href = url;
    } catch (e: any) {
      setError(e.message || "Erreur lors de la récupération de l'URL CAS");
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!verified) return;
    setCompleting(true);
    setError(null);
    try {
      await studentRegistrationService.completeRegistration(gitlabUsername.trim(), verified.access_token);
      setCompleted(true);
    } catch (e: any) {
      setError(e.message || "Erreur lors de l'enregistrement du pseudo GitLab");
    } finally {
      setCompleting(false);
    }
  };

  return (
    <LoginLayout>
      <Paper sx={{ p: 4, maxWidth: 480 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
          Inscription au module Technologies Web
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !verified && (
          <>
            <Typography sx={{ mb: 3 }}>
              Connectez-vous avec vos identifiants universitaires (SSO CAS) pour confirmer votre
              compte et votre pseudo GitLab.
            </Typography>
            <Button variant="contained" fullWidth onClick={handleCasLogin}>
              Se connecter via CAS
            </Button>
          </>
        )}

        {!loading && verified && !completed && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              Identité confirmée : {verified.firstname} {verified.lastname} ({verified.login})
            </Alert>
            <Typography sx={{ mb: 2 }}>
              Vérifiez votre pseudo GitLab (celui utilisé sur {' '}
              <code>gitlab2.istic.univ-rennes1.fr</code>) et corrigez-le si besoin pour finaliser
              votre inscription.
            </Typography>
            <TextField
              label="Pseudo GitLab"
              fullWidth
              value={gitlabUsername}
              onChange={(e) => setGitlabUsername(e.target.value)}
              helperText="Pré-rempli avec votre login universitaire — à corriger s'il diffère de votre pseudo GitLab"
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              fullWidth
              disabled={!gitlabUsername.trim() || completing}
              onClick={handleComplete}
            >
              {completing ? <CircularProgress size={20} /> : 'Confirmer'}
            </Button>
          </>
        )}

        {completed && (
          <Alert severity="success">
            Inscription terminée{verified ? ` : ${verified.firstname} ${verified.lastname} (${verified.login})` : ''} !
            Vous pouvez fermer cette page.
          </Alert>
        )}
      </Paper>
    </LoginLayout>
  );
};

export default StudentRegistrationPage;
